import { useState } from 'react';
import { Smartphone, ChevronDown } from 'lucide-react';
import { Pill } from '@nako/ui';
import {
  MOBILE_MONEY_PROVIDERS,
  providersForRegion,
  COLLECTION_PARTNER,
  type RegionCode,
  type MobileMoneyProvider,
} from '@nako/core';
import { useUiStore } from '@/stores/ui';
import { makeT } from '@/lib/i18n';

interface Props {
  /** When set, show wallets for this region only; otherwise all three live markets. */
  region?: RegionCode | null;
}

/**
 * Landing-page showcase of mobile-money providers we support across
 * the BW / ZA / NG markets. Display-only — the actual collection /
 * payout integration goes through RealPay Collect Mobile (Alpha Direct
 * partner) and is not wired into the app yet.
 *
 * The component pulls from packages/core/mobile-money.ts so the same
 * provider list drives the marketing showcase, the in-app payment
 * picker (future), and any analytics / signed support attestations
 * we publish.
 */
export function MobileMoneyShowcase({ region = null }: Props) {
  const providers: MobileMoneyProvider[] = region
    ? providersForRegion(region, { includeAggregators: true })
    : MOBILE_MONEY_PROVIDERS;
  const lang = useUiStore((s) => s.lang);
  const t = makeT(lang);

  const [expanded, setExpanded] = useState(false);
  const preview = providers.slice(0, 8);
  const overflow = providers.length - preview.length;

  return (
    <section
      aria-labelledby="mobile-money-heading"
      className="border-line bg-paper-2/60 mx-auto mt-10 max-w-5xl rounded-2xl border p-5 md:p-7"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Smartphone size={18} className="text-accent" />
          <h2 id="mobile-money-heading" className="font-display text-lg">
            {t('mm.title')}
          </h2>
        </div>
        <Pill tone="accent">{t('mm.coming_soon')}</Pill>
      </header>

      <p className="text-muted mx-auto mt-3 max-w-2xl text-sm">
        {t('mm.copy')}{' '}
        <a
          href={COLLECTION_PARTNER.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent underline-offset-2 hover:underline"
        >
          {COLLECTION_PARTNER.name}
        </a>
        .
      </p>

      <ul
        role="list"
        className="mt-5 flex flex-wrap items-center justify-center gap-2"
        aria-label="Mobile-money providers supported"
      >
        {(expanded ? providers : preview).map((p) => (
          <li key={p.id}>
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium"
              style={{
                backgroundColor: p.brandColor,
                color: p.brandTextColor,
              }}
              title={`${p.name} · ${p.operator} · ${p.regions.join(' / ')}`}
            >
              {p.name}
              <span className="opacity-75">· {p.regions.join('/')}</span>
            </span>
          </li>
        ))}
        {!expanded && overflow > 0 && (
          <li>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="border-line text-muted hover:text-ink inline-flex items-center gap-1 rounded-full border bg-white px-3 py-1.5 text-xs font-medium"
            >
              + {overflow} more
              <ChevronDown size={14} />
            </button>
          </li>
        )}
      </ul>

      <p className="text-muted/80 mt-4 text-center text-[11px]">{t('mm.disclaimer')}</p>
    </section>
  );
}
